import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { findCurCItyInfo, levelEnum } from '../../ts-config';
import styles from './index.module.less';
import IconFont from '@/common/components/IconFont';
import { Modal } from 'antd';
import V2ProvincePanel from '@/common/components/Data/V2ProvincePanel';
import { useDispatch, useSelector } from 'react-redux';
import { getMainCity } from '@/common/api/siteselectionmap';
import V2CityIndexBar from '@/common/components/Data/V2CityIndexBar';
import useUpdateEffect from '@/common/hook/updateEffect/useUpdateEffect';
import { areaList } from '@/common/api/common';
import { changeProvincesCities } from '@/store/common';

/**
 * @Description 省市卡片
 */
const ProvinceCityCard:FC<{
  viewLevel,
  setViewLevel,
  isGreater1440,
  isGreater1920,
}> = ({
  viewLevel,
  setViewLevel,
  isGreater1440,
  isGreater1920,
}) => {
  const dispatch = useDispatch();
  // 省份
  const [provinceActiveValue, setProvinceActiveValue] = useState<string | number>(0);
  const [provinceVisible, setProvinceVisible] = useState<boolean>(false);

  // 城市
  const [cityVisible, setCityVisible] = useState<boolean>(false);// 城市选择弹窗开关
  const [mainCity, setMainCity] = useState<any[]>([]);// 主力城市
  const [checkedValue, setCheckedValue] = useState<any>({});

  const provinces = useSelector((state: any) => state.common.provincesCities);

  const isViewChoosingRef = useRef<boolean>(false);// 判断是否在选择视图的整体流程中
  const domRef = useRef<any>(null);// 缩放效果

  useEffect(() => {
    const getAreaList = async () => {
      const cityResult: any = await areaList({ type: 2 });
      dispatch(changeProvincesCities(cityResult));
    };
    // 如果不存在则进行请求
    if (!provinces.length) {
      getAreaList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 点击省份、市名称
  const handleClick = () => {
    if (viewLevel.level === levelEnum.province) {
      setProvinceVisible(true);
    }
    if ([levelEnum.city, levelEnum.site].includes(viewLevel?.level)) {
      setCityVisible(true);
    }
  };

  // 显示内容
  const content = () => {
    const provinceName = provinces?.find((item) => item.id === viewLevel?.provinceId)?.name;
    if (viewLevel.level === levelEnum.province) {
      return provinceName;
    } else {
      return provinceName === checkedValue?.name ? checkedValue?.name : `${provinceName}/${checkedValue?.name}`;
    }
  };
  const handleChooseCity = (value) => {
    if (!value?.lng) return;
    // mapIns?.setZoomAndCenter(13, [+value?.lng, value?.lat], false, 100);
    setCheckedValue(value);
    handleZoomAnimation();
  };
  const handleChooseProvince = (value) => {
    if (!value?.lng) return;
    // mapIns?.setZoomAndCenter(9, [+value?.lng, value?.lat], false, 100);
    handleZoomAnimation();
  };

  const handleZoomAnimation = () => {
    if (isViewChoosingRef.current) {
      domRef.current.classList.add(styles.zoomAnimation); // 添加包含缩放动画的CSS类
      // 缩放动画结束后，移除CSS类，否则会由于这个页面具有缓存，在切换页面后，动画会再次执行
      setTimeout(() => {
        domRef.current.classList.remove(styles.zoomAnimation);
      }, 3000);
      isViewChoosingRef.current = false;
    }
  };

  // 主力城市
  const extraCities = useMemo(() => {
    if (!mainCity?.length || !provinces?.length) {
      return null;
    } else {
      const mainCityIds = mainCity.map((item) => item.id);
      let res:any = [];

      provinces?.map((curProvince) => {
        curProvince.children.map((city) => {
          const index = mainCityIds.findIndex((item) => item === city.id);
          if (index !== -1) {
            res.push({
              name: city?.name || curProvince.name,
              id: city?.id,
              code: city?.code,
              lng: city?.lng,
              lat: city?.lat,
              pinyin: curProvince?.pinyin,
              initial: curProvince?.pinyin?.charAt(0)?.toUpperCase(),
              index
            });
          }
        });
      });
      res = res.sort((a, b) => a.index - b.index);
      return [
        {
          title: '主力城市',
          cities: res
        }
      ];
    }
  }, [mainCity, provinces]);

  // 如果省份变化，则改变ProvinceActiveValue(同步外部的provinceId到ProvinceActiveValue)
  useEffect(() => {
    if (viewLevel?.provinceId !== provinceActiveValue &&
      viewLevel?.provinceId &&
      viewLevel?.level === levelEnum.province) {
      setProvinceActiveValue(viewLevel.provinceId);
    }
  }, [viewLevel?.provinceId, viewLevel?.level]);

  // 如果省份变化，则改变CheckedValue(同步外部的cityId到checkedValue)
  useEffect(() => {
    if (viewLevel?.cityId !== checkedValue?.id &&
      viewLevel?.cityId &&
      [levelEnum.city, levelEnum.site]?.includes(viewLevel?.level)) {
      const cityInfo = findCurCItyInfo(provinces, viewLevel?.cityId);
      if (!cityInfo) return;
      setCheckedValue({
        name: cityInfo.name,
        id: cityInfo.id,
        code: cityInfo.code,
        lng: cityInfo.lng,
        lat: cityInfo.lat,
        pinyin: cityInfo.pinyin,
        initial: cityInfo?.pinyin?.charAt(0)?.toUpperCase(),
      });

    }
  }, [viewLevel?.level, viewLevel?.cityId, provinces]);

  // 选择省份后的变化
  useUpdateEffect(() => {
    setViewLevel({
      level: levelEnum.province,
      provinceId: +provinceActiveValue
    });
    setProvinceVisible(false);
  }, [provinceActiveValue]);

  // 选择城市后的变化
  useUpdateEffect(() => {
    if (!checkedValue?.id) return;
    const { provinceId } = findCurCItyInfo(provinces, checkedValue?.id);
    setViewLevel((state) => ({
      ...state,
      provinceId: +provinceId,
      cityId: +checkedValue?.id
    }));
  }, [checkedValue?.id]);

  useEffect(() => {
    // 当cityId不存在或已有主力城市数据，则不再请求
    if (!viewLevel?.cityId || mainCity?.length) return;
    getMainCity().then((val) => {
      setMainCity(val);
    });
  }, [viewLevel?.cityId]);
  return <>
    {
      [levelEnum.province, levelEnum.city, levelEnum.site]?.includes(viewLevel.level)
        ? <><div
          onClick={() => handleClick()}
          className={styles.provinceCityCard}
          style={{
            width: `calc(${isGreater1440 ? isGreater1920 ? '194px' : '10.0vw' : '146px'}`,
          }}
        >
          <span className={styles.content}>{content()}</span>
          <IconFont
            iconHref='pc-common-icon-a-iconarrow_down'
            className={styles.icon}
          />
        </div>
        </>
        : <></>
    }
    {/* 省份选择 */}
    <Modal
      title='选择省份'
      open={provinceVisible}
      width={560}
      onCancel={() => setProvinceVisible(false)}
      forceRender
      footer={null}
      maskClosable={!isViewChoosingRef.current}
      keyboard={!isViewChoosingRef.current}
      closable={!isViewChoosingRef.current}
      centered={true}
    >
      <V2ProvincePanel
        activeValue={provinceActiveValue}
        setActiveValue={setProvinceActiveValue}
        onChange={handleChooseProvince}
      />
    </Modal>

    {/* 城市选中 */}
    <V2CityIndexBar
      extraCities={extraCities || undefined}
      open={cityVisible}
      setOpen={setCityVisible}
      checkedValue={checkedValue}
      setCheckedValue={handleChooseCity}
      modalProps={{
        maskClosable: !isViewChoosingRef.current,
        keyboard: !isViewChoosingRef.current,
        centered: true,
        bodyStyle: {
          minHeight: 400
        }
      }}
    />
  </>;
};
export default ProvinceCityCard;
